import React, { useCallback, useRef, useState, useEffect } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { H2, H3, Text } from "../typography";
import { Expander } from "../expander";
import { TaskAction } from "./action";
import { isTaskEnabled, Task, TaskGroup } from "../../config/tasks";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import { Status } from "../status";
import { StatusEnum, withStatus } from "../../hoc/with-status";
import { useParams } from "react-router";
import { isDragonstone } from "../../helpers/url-params";
import { getJobScheduleDetails } from "../../utility/application-data";
import useArb from "../../hooks/use-arb";
import { Stage } from "../../helpers/get-settings";

export interface PopoverProps {
  title: string;
  body?: string[];
  buttonText?: string;
}

interface TasksGroupProps {
  data: TaskGroup;
  applicationId: string;
  candidateId: string;
  stage: Stage;
}

interface TaskProps {
  task: Task;
  applicationId: string;
  candidateId: string;
  bundle: any;
  stage: Stage;
}

export const TaskItem = ({
  task,
  applicationId,
  candidateId,
  bundle,
  stage,
}: TaskProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [businessLine, setBusinessLine] = useState("");
  let enableTask = false;
  // TODO Make the enable/disable task more generic for all tasks and use app config.
  const DisabledBusinessLines = ["AIR"];
  const EnabledBusinessLines = ["AIR"];

  const target = useRef(null);
  const { requisitionId } = useParams<{
    requisitionId: string;
  }>();
  const onToggle = useCallback(
    (isExpanded: boolean) => {
      setIsExpanded(isExpanded);
      if (isExpanded && task.analyticsOnExpand) {
        if (task.getTaskStatusByBBId) {
          adobeAnalytics.addEventMetricWithBBId(
            window,
            applicationId,
            "click text",
            {
              text: task.analyticsOnExpand,
              jobId: requisitionId,
              isDragonstone: isDragonstone(requisitionId),
            }
          );
        } else {
          adobeAnalytics.addEventMetric(window, applicationId, "click text", {
            text: task.analyticsOnExpand,
            jobId: requisitionId,
            isDragonstone: isDragonstone(requisitionId),
          });
        }
      }
    },
    [task, applicationId, candidateId, requisitionId]
  );

  useEffect(() => {
    (async () => {
      const scheduleDetails = await getJobScheduleDetails(applicationId);
      setBusinessLine(scheduleDetails?.businessLine as string);
    })();
  }, []);

  const renderExpander = (status?: StatusEnum, taskId?: string) => {
    const shouldExpandOnMount =
      task.expandWhenNotStarted && status === StatusEnum.NOT_STARTED;

    const taskDetails =
      typeof task.details === "function"
        ? task.details(status || StatusEnum.NOT_STARTED, isExpanded, taskId)
        : task.details;

    return (
      <Expander
        key={task.titleText}
        title={task.titleText}
        titleMod={status && <Status status={status} />}
        onToggle={onToggle}
        shouldExpandOnMount={shouldExpandOnMount}
      >
        <Row>
          <Col>
            <Text>{taskDetails}</Text>
          </Col>
          <Col ref={target}></Col>
        </Row>
        {task.href && (
          <Row>
            <TaskAction
              task={task}
              applicationId={applicationId}
              disabled={
                status === StatusEnum.COMPLETED && task.disableWhenCompleted
              }
            />
          </Row>
        )}
        {task.subtasks &&
          task.subtasks.map((subtaskStatus, i) => {
            const renderSubtask = (status?: StatusEnum) => {
              return (
                <div key={i} className={"subtask-container"}>
                  <H3>
                    {subtaskStatus.titleText}
                    {status ? (
                      <>
                        {" "}
                        <Status status={status} />
                      </>
                    ) : null}
                  </H3>
                  {subtaskStatus.errorStatusDetailMessage &&
                  status == StatusEnum.ERROR
                    ? subtaskStatus.errorStatusDetailMessage
                    : null}
                  {status &&
                  subtaskStatus.statusDetailMessage &&
                  subtaskStatus.statusDetailMessage[status]
                    ? subtaskStatus.statusDetailMessage[status]
                    : null}
                </div>
              );
            };

            let enableSubTask = false;
            if (
              isTaskEnabled(subtaskStatus.taskName as string, "SUBTASK", stage)
            ) {
              if (
                subtaskStatus.checkBusinessLine &&
                !EnabledBusinessLines.includes(businessLine)
              ) {
                enableSubTask = false;
              } else {
                enableSubTask = true;
              }
            }

            const subtask = withStatus({
              key: i.toString(),
              statusPath: subtaskStatus.titleStatusPath,
              candidateId,
              applicationId,
              renderComponent: renderSubtask,
              bundle,
              enableTask: enableSubTask,
            });

            return subtask;
          })}
      </Expander>
    );
  };

  if (isTaskEnabled(task.taskName as string, "TASK", stage)) {
    if (
      task.checkBusinessLine &&
      DisabledBusinessLines.includes(businessLine)
    ) {
      enableTask = false;
    } else {
      enableTask = true;
    }
  }

  return withStatus({
    key: task.titleText,
    statusPath: task.titleStatusPath,
    taskStatusName: task.taskStatusName,
    getTaskStatusByBBId: task.getTaskStatusByBBId,
    applicationId,
    candidateId,
    hideWhenError: task.hideWhenError,
    isFallback: task.isFallback,
    renderComponent: renderExpander,
    shouldOverrideStatus: task.shouldOverrideStatus,
    hideOnTaskStatusError: task.hideOnTaskStatusError,
    enableTask: enableTask,
    bundle,
  });
};

export const TasksGroup = ({
  data,
  applicationId,
  candidateId,
  stage,
}: TasksGroupProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  return stage ? (
    <Col gridGap="1rem">
      <H2>{data.name}</H2>
      {data.tasks.map((task, index) => (
        <TaskItem
          key={task.titleText + index}
          task={task}
          applicationId={applicationId}
          candidateId={candidateId}
          bundle={bundle}
          stage={stage}
        />
      ))}
    </Col>
  ) : null;
};
